import type {
    BulkDeleteRequest,
    ChannelStatusResponse,
    Notification,
    NotificationListResponse,
    NotificationQuery,
    PushSubscriptionRequest,
    TelegramLinkCodeResponse,
    TelegramStatusResponse,
    UnreadCountResponse,
} from '@/types/notification'
import { api } from './client'

const BASE_PATH = '/v1/notifications'

function buildQuery(params?: Record<string, string | number | undefined>): string {
  if (!params) return ''
  const qs = new URLSearchParams()
  for (const [key, val] of Object.entries(params)) {
    if (val !== undefined && val !== '') qs.set(key, String(val))
  }
  const str = qs.toString()
  return str ? `?${str}` : ''
}

export const notificationApi = {
  /** List notifications (paginated, filtered) */
  list: (query?: NotificationQuery) => {
    const params: Record<string, string | number | undefined> = {
      page: query?.page,
      perPage: query?.perPage,
      eventType: query?.eventType,
      status: query?.status,
      channel: query?.channel,
      since: query?.since,
    }
    return api.get<NotificationListResponse>(`${BASE_PATH}${buildQuery(params)}`)
  },

  /** Get single notification */
  get: (id: string) =>
    api.get<Notification>(`${BASE_PATH}/${id}`),

  /** Mark notification as read */
  markRead: (id: string) =>
    api.post<void>(`${BASE_PATH}/${id}/read`),

  /** Mark all notifications as read */
  markAllRead: () =>
    api.post<void>(`${BASE_PATH}/read-all`),

  /** Delete notification */
  delete: (id: string) =>
    api.delete<void>(`${BASE_PATH}/${id}`),

  /** Bulk delete notifications */
  bulkDelete: (data: BulkDeleteRequest) =>
    api.post<void>(`${BASE_PATH}/bulk`, data),

  /** Get unread count */
  unreadCount: () =>
    api.get<UnreadCountResponse>(`${BASE_PATH}/unread-count`),

  /** Get channel delivery status */
  channelStatus: () =>
    api.get<ChannelStatusResponse>(`${BASE_PATH}/channels/status`),

  // ── Telegram ───────────────────────────────────────────────────────

  /** Generate Telegram linking code */
  telegramGenerateCode: () =>
    api.post<TelegramLinkCodeResponse>(`${BASE_PATH}/telegram/generate-code`),

  /** Check Telegram link status */
  telegramStatus: () =>
    api.get<TelegramStatusResponse>(`${BASE_PATH}/telegram/status`),

  /** Unlink Telegram account */
  telegramUnlink: () =>
    api.delete<void>(`${BASE_PATH}/telegram/unlink`),

  // ── Push ───────────────────────────────────────────────────────────

  /** Register push subscription */
  pushSubscribe: (data: PushSubscriptionRequest) =>
    api.post<void>(`${BASE_PATH}/push/subscribe`, data),

  /** Remove push subscription */
  pushUnsubscribe: () =>
    api.delete<void>(`${BASE_PATH}/push/unsubscribe`),
}
